/**
 * MarqueeRail — iter-96
 *
 * Wraps a horizontal pill row and auto-scrolls it slowly to the right so
 * users see ALL options without manually swiping. Pauses on touch / hover /
 * keyboard focus; resumes after ~1.5s of inactivity.
 *
 * Usage:
 *   <MarqueeRail>
 *     <button data-testid="tone-auto">…</button>
 *     <button data-testid="tone-bold">…</button>
 *     …
 *   </MarqueeRail>
 *
 * Mechanics:
 *   • Renders the children TWICE so the second set seamlessly fills in as
 *     the first scrolls out — perpetual marquee with no jump.
 *   • Auto-scroll uses requestAnimationFrame on `scrollLeft += pxPerFrame`.
 *   • Width-aware: only auto-scrolls when content overflows.
 *   • Active item can still be tapped at any time.
 *
 * Accessibility:
 *   • Respects `prefers-reduced-motion: reduce` → no auto-scroll.
 *   • Keyboard focus + touch pause it immediately.
 */
import React, { useEffect, useRef, useState } from "react";

export default function MarqueeRail({
    children,
    speed = 0.25,           // pixels per RAF frame (~60fps). 0.25 ≈ 15 px/s — slow.
    pauseOnHover = true,
    className = "",
    contentClassName = "",
    testId = "marquee-rail",
}) {
    const wrapperRef = useRef(null);
    const trackRef = useRef(null);
    const rafRef = useRef(null);
    const pausedRef = useRef(false);
    const lastInteractionRef = useRef(0);
    const [overflows, setOverflows] = useState(false);
    const [reducedMotion, setReducedMotion] = useState(false);

    // Respect reduced-motion preference.
    useEffect(() => {
        try {
            const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
            const sync = () => setReducedMotion(mq.matches);
            sync();
            mq.addEventListener?.("change", sync);
            return () => mq.removeEventListener?.("change", sync);
        } catch { /* ignore */ }
    }, []);

    // Detect overflow — only marquee when content is wider than the wrapper.
    useEffect(() => {
        const checkOverflow = () => {
            const wrap = wrapperRef.current;
            const track = trackRef.current;
            if (!wrap || !track) return;
            // Track holds 2 copies, so single-copy width = scrollWidth / 2.
            const singleWidth = track.scrollWidth / 2;
            setOverflows(singleWidth > wrap.clientWidth + 4);
        };
        checkOverflow();
        const ro = new ResizeObserver(checkOverflow);
        if (wrapperRef.current) ro.observe(wrapperRef.current);
        if (trackRef.current) ro.observe(trackRef.current);
        return () => ro.disconnect();
    }, [children]);

    // Auto-scroll loop.
    useEffect(() => {
        if (reducedMotion || !overflows) return;
        const wrap = wrapperRef.current;
        const track = trackRef.current;
        if (!wrap || !track) return;
        const singleWidth = () => track.scrollWidth / 2;

        const tick = () => {
            // If recently interacted with, hold position.
            const sincePause = Date.now() - lastInteractionRef.current;
            if (pausedRef.current && sincePause < 1500) {
                rafRef.current = requestAnimationFrame(tick);
                return;
            }
            pausedRef.current = false;
            wrap.scrollLeft += speed;
            // When we've scrolled past the first copy, jump back by exactly
            // one copy-width so the loop is invisible.
            const w = singleWidth();
            if (wrap.scrollLeft >= w) {
                wrap.scrollLeft -= w;
            }
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    }, [overflows, reducedMotion, speed]);

    const pause = () => {
        pausedRef.current = true;
        lastInteractionRef.current = Date.now();
    };

    return (
        <div
            ref={wrapperRef}
            data-testid={testId}
            onTouchStart={pause}
            onTouchMove={pause}
            onWheel={pause}
            onMouseEnter={pauseOnHover ? pause : undefined}
            onFocusCapture={pause}
            className={
                "overflow-x-auto scrollbar-none " +
                "[scrollbar-width:none] [-ms-overflow-style:none] " +
                "[&::-webkit-scrollbar]:hidden " +
                className
            }
        >
            <div
                ref={trackRef}
                className={"flex flex-nowrap items-center gap-2 w-max " + contentClassName}
            >
                {children}
                {overflows && !reducedMotion && children}
            </div>
        </div>
    );
}
